// SPDX-License-Identifier: AGPL-3.0-or-later

import {reuseGameActivitySnapshot, type GameActivityPresence} from '@app/features/game_activity/GameActivity';
import {GameActivityEmitter} from '@app/features/game_activity/GameActivityEmitter';
import GameActivityStore from '@app/features/game_activity/GameActivityStore';
import Presence from '@app/features/presence/state/Presence';
import Authentication from '@app/features/auth/state/Authentication';
import {isOfflineStatus} from '@fluxer/constants/src/StatusConstants';
import {useCallback, useRef, useSyncExternalStore} from 'react';

export function useGameActivityPresence(userId: string, enabled = true): GameActivityPresence | null {
	const subscribe = useCallback(
		(onStoreChange: () => void) => {
			if (!enabled || !userId) {
				return () => {};
			}
			return GameActivityEmitter.subscribeToPresence(userId, onStoreChange);
		},
		[enabled, userId],
	);

	const snapshotRef = useRef<GameActivityPresence | null>(null);
	const getSnapshot = useCallback((): GameActivityPresence | null => {
		if (!enabled || !userId) {
			snapshotRef.current = null;
			return null;
		}
		let next: GameActivityPresence | null;
		if (userId === Authentication.currentUserId) {
			next = GameActivityStore.getPresenceActivity();
		} else if (isOfflineStatus(Presence.getStatus(userId))) {
			next = null;
		} else {
			next = Presence.getGameActivity(userId);
		}
		snapshotRef.current = reuseGameActivitySnapshot(snapshotRef.current, next);
		return snapshotRef.current;
	}, [enabled, userId]);

	return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
